import { useState, useMemo } from 'react';
import { MessageCircle } from 'lucide-react';
import { useConversations, useConversationActions } from '../hooks/useWhatsapp';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import ToastContainer from '../components/common/Toast';
import ConversationList from '../components/whatsapp/ConversationList';
import ThreadView from '../components/whatsapp/ThreadView';
import EmptyState from '../components/whatsapp/EmptyState';

const Whatsapp = () => {
  const { toasts, removeToast, success, error } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const debouncedSearch = useDebounce(search, 300);
  const filters = useMemo(
    () => ({ search: debouncedSearch || undefined, status: status || undefined, size: 30 }),
    [debouncedSearch, status]
  );

  const { toggleBot, resolve, handBack, takeOver, reply } = useConversationActions();
  const { data, isLoading, isError } = useConversations(filters);
  const conversations = data?.content || [];
  const total = data?.totalElements;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-50 text-green-600">
            <MessageCircle className="h-5 w-5" />
          </span>
          Messagerie WhatsApp
        </h1>
        <div className="flex items-center justify-between">
          <p className="mt-1 text-sm text-gray-500">
            Suivez les conversations, reprenez la main sur le bot et répondez aux clients.
          </p>
          <a
            href="/admin/whatsapp/settings"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Paramètres IA
          </a>
        </div>
      </div>

      <div className="flex h-[calc(100vh-12rem)] min-h-[480px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Conversation list (full width on mobile until one is selected) */}
        <div
          className={`${selectedId ? 'hidden lg:flex' : 'flex'} w-full flex-col border-r border-gray-200 lg:w-[360px] lg:flex-shrink-0`}
        >
          <ConversationList
            conversations={conversations}
            total={total}
            isLoading={isLoading}
            isError={isError}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onToggleBot={(id) => toggleBot.mutate(id)}
            search={search}
            onSearchChange={setSearch}
            status={status}
            onStatusChange={setStatus}
          />
        </div>

        {/* Thread + composer */}
        <div className={`${selectedId ? 'flex' : 'hidden lg:flex'} min-w-0 flex-1 flex-col`}>
          {selectedId ? (
            <ThreadView
              conversationId={selectedId}
              onBack={() => setSelectedId(null)}
              success={success}
              error={error}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default Whatsapp;
